// تونسي — Tunisian Derja. The vibe: the lobby is the neighbourhood café
// ("القهوة"), a room is a game ("طرح", plural "أطراح") and the dealer is the
// waiter ("القهواجي"). Card words follow what players actually say at the table:
// بيوشي (pioche), الكُدس (discard pile), منع (Nemna3), فرش (Nefrech — written
// without shadda). Redeal vote: فريش = rejouer, ألعب = continuer.
// الجولة = manche, الطرح = partie.

// Tunisian counting: 1 → singular, 2 → "زوز" + plural, 3–10 → plural, 11+ → singular.
const count = (n, one, two, few, many) =>
  n === 1 ? one : n === 2 ? two : n >= 3 && n <= 10 ? `${n} ${few}` : `${n} ${many}`;

const cards = (n) => count(n, "ورقة وحدة", "زوز أوراق", "أوراق", "ورقة");
const bots = (n) => count(n, "بوت واحد", "زوز بوتات", "بوتات", "بوت");
const groups = (n) => count(n, "مجموعة وحدة", "زوز مجموعات", "مجموعات", "مجموعة");
const players = (n) => count(n, "لاعب واحد", "زوز لاعبين", "لاعبين", "لاعب");
const seconds = (n) => count(n, "ثانية", "زوز ثواني", "ثواني", "ثانية");
// Verb agreement: one card "تزادت", several "تزادو".
const sg = (n, one, many) => (n === 1 ? one : many);
// A run of cards (A-2-3) never splits over two lines: a word joiner after each hyphen.
const run = (cards) => cards.replace(/-/g, "-⁠");

const tn = {
  meta: { title: "رامي أونلاين — الرامي مع الأصحاب" },

  lang: {
    label: "اللغة",
    switched: "عسلامة! 👋 اللعبة ولّات بالتونسي",
  },

  common: {
    brand: "رامي أونلاين",
    lobby: "القهوة",
    error: "صار مشكل",
    loading: "استنّى شويّة…",
    cancel: "بطّل",
    or: "ولّا",
    back: "أرجع",
    home: "روّح",
    you: "إنت",
    me: "أنا",
    yes: "إيه",
    no: "لا",
    left: "اليسار",
    right: "اليمين",
    pts: "{n} بونتو",
    playersCount: "{a}/{b} لاعبين",
    networkRetry: "الريزو طايح، عاود جرّب",
    connecting: "قاعدين ندخلو…",
    // Google sign-in in the apps (lib/googleLogin.js).
    googleFailed: "الدخول بـ Google ما نجحش. عاود جرّب.",
    piocheSide: "بلاصة البيوش",
    google: "كمّل بـ Google",
    namePlaceholder: "إكتب إسمك…",
    enterName: "اكتب اسمك قبل",
    joinArrow: "ادخل ←",
    quit: "خروج",
    join: "ألعب",
    inProgress: "يتلعب توّا",
    eliminated: "برّا",
    threshold: "المنعة",
    elim: "الخروج",
    jokers: "جوكرات",
    rematch: "طرح آخر",
    rematchBadge: "روفانش",
    rematchWaiting: ({ n }) =>
      n > 1 ? "{names} يستنّاو فيك لطرح آخر 🔁" : "{names} يستنّى فيك لطرح آخر 🔁",
  },

  landing: {
    titlePre: "",
    titleMain: "رامي أونلاين",
    subtitle: "مع الأصحاب و الأحباب",
    guestTitle: "ألعب كضيف",
    // Proposed by Claude, not yet confirmed by the owner.
    googleKeep: "الرتبة و النتايج متاعك تلقاهم في أي جهاز",
    guestKept: "الحساب يتحفظ في الجهاز هذا فقط",
    resumeAs: "كمّل باسم",
    newGuest: "حساب ضيف جديد",
    replaceWarn: "حساب {name} باش يتفسخ، بالرتبة متاعو.",
    guestGone: "حساب الضيف هذا ما عادش موجود: أكتب اسمك باش تعمل واحد جديد.",
    // No second line under the guest button in Tunisian (owner's choice).
    guestSub: [],
    nameEnough: "اسمك برك ويزّي باش تدخل",
    continueArrow: "كمّل ←",
    // No sentence under "بلاصة البيوش" in Tunisian (the owner trims these).
    piocheHint: [],
    connecting: "قاعدين ندخلو…",
    feat: {
      players: "من 2 لـ 4 لاعبين",
      bots: "بوتات تلعب معاك",
      realtime: "لعب لايف",
    },
  },

  lobby: {
    // Proposed by Claude, not yet confirmed by the owner (create = إبدأ طرح, friends = أصحاب, Lobby = القهوة).
    hub: {
      ongoing: "طرح ماشي",
      onlineTag: "4 لاعبين",
      onlineSub: "نلقاولك 3 لاعبين في ثواني. يتحسب للرتبة متاعك.",
      openTables: ({ n }) => (n === 0 ? "حتى طاولة مفتوحة" : n === 1 ? "طاولة وحدة مفتوحة" : `${n} طاولات مفتوحة`),
      friendsTitle: "مع الأصحاب",
      friendsSub: "طاولتك، قوانينك، و كود تبعثو",
      create: "إبدأ طرح",
      yourTables: "طاولاتك",
      trainingTitle: "تدريب",
      trainingSub: "ما يتحسبش للرتبة متاعك",
      solo2: "واحد ضد واحد",
      solo4: "4 لاعبين",
      level: "مستوى البوتات",
      learnTitle: "كيفاش نلعب؟",
      learnSub: "القوانين في 9 مراحل، و من بعد طرح تجربة",
      tablePreview: "طاولتك، كيما باش تشوفها في اللعب.",
    },
    code: {
      button: "إستعمل الكود",
      placeholder: "كود الطرح ولّا لينك الدعوة",
      notFound: "ما لقيناش الطرح — ثبّت في الكود ولّا اللينك",
      inProgress: "يتلعب توّا",
    },
    solo1v1: "ألعب ضدّ بوت",
    solo4: "ألعب ضدّ 3 بوتات",
    quitFirst: "اخرج من الطرح إلّي تلعب فيه قبل",
    create: "إبدأ طرح",
    createShort: "إبدأ",
    newGame: "طرح جديد",
    players: "اللاعبين",
    nPlayers: "{n} لاعبين",
    botsLabel: "بوتات ({n} / {max} ماكس)",
    nBots: ({ n }) => (n === 0 ? "بلاش بوتات" : bots(n)),
    botLevel: "مستوى البوتات",
    level: { debutant: "مبتدي 🐣", medium: "متوسّط 😏", legende: "أسطورة 🔥" },
    nJokers: "{n} جوكرات",
    thresholdLabel: "المنعة الأولى",
    thresholdTip:
      "أقلّ بونتو لازم يكون عندك باش تمنع أوّل مرّة. تحت الحدّ هذا ما تنجّمش تنزّل أوراقك.",
    elimLabel: "حدّ الخروج",
    elimTip:
      "إلّي يفوت البونتو هذا يخرج برّا من الطرح. وآخر واحد يقعد هو إلّي يربح؛ بـ4 لاعبين، آخر زوز يربحو، مرتّبين على حساب البونتو.",
    rulesTitle: "الخطايا والقوانين",
    pureSeq: "سويت من غير جوكر",
    pureSeqTip:
      "كي تتحلّ، لازم في الأوراق إلّي تنزّلهم على الأقل سويت وحدة من نفس اللون ومن غير جوكر.",
    pen: {
      standard: {
        label: "الخطيّة العاديّة",
        tip: "بونتو يتزادو للاعب إلّي يغلط في وسط اللعب — مثلاً يبدا ينزّل في أوراقو على أساس وصل للمنعة، ومبعد يبطّل.",
      },
      joker: {
        label: "جوكر في اليد",
        tip: "خطيّة للاعب إلّي منع وما ربحش الجولة وقعد شادّ جوكر في اليد.",
      },
      useful: {
        label: "طيّش ورقة بلاس",
        tip: "خطيّة كي تطيّش ورقة كانت تنجّم تتزاد على مجموعة محطوطة.",
      },
      continuer: {
        label: "شدّ الفريش",
        tip: "خطيّة للاعب إلّي شدّ الفريش وقال ألعب، وفي الآخر لا منع لا ربح الجولة.",
      },
      nefrechFausse: {
        label: "تفرش من كف صاحبك",
        tip: "خطيّة عليك كي تطيّش ورقة ويهزّها إلّي بعدك ويفرش بيها.",
      },
    },
    createInvite: "إبدأ طرح وإستدعى أصحابك",
    createCancel: "أرجع",
    playOnline: "ألعب أونلاين",
    play: "ألعب",
    searching: "نلوّجو…",
    privateTitle: "أطراح خاصّة",
    privateDesc: "إبدأ طرح خاصّ، ابعث اللينك لأصحابك، وحطّ القوانين إلّي تحبّ عليهم.",
    chip: {
      link: "دعوة باللينك",
      rules: "قوانين على كيفك",
      friends: "من 2 لـ 4 أصحاب",
      // Empty: the owner removed these two badges in Tunisian (shown in French only).
      active: [],
      botReplace: "إلّي يطيح عليه الريزو، بوت يلعب في بلاصتو",
      players4: "4 لاعبين",
      instant: [],
      realtime: "لايف",
    },
    resume: "أرجع للطرح ←",
    presetDefault: "عادي",
    presetCustom: "خاصّ",
    viewParams: "شوف القوانين",
    copyLink: "انسخ اللينك",
    linkCopied: "اللينك تنسخ! ✓",
    // A room was just created: its code is in the clipboard (Lobby.handleCreate).
    // Proposed by Claude (reuses the owner's أبعث), not yet confirmed.
    codeCopied: "الكود {code} تنسخ! أبعثو لأصحابك 📲",
    full: "معبّي",
    noPrivate: "ما فمّا حتى طرح خاصّ محلول",
    noPrivateSub: "إبدأ طرح وابعث اللينك لأصحابك",
    onlineTitle: "أطراح أونلاين",
    available: "{n} محلولة",
    onlineDesc: "ألعب طرح بـ4 لاعبين لايف. أحنا نلوّجولك على بلاصة.",
    hostJokers: "{host} · {n} جوكرات",
    spotsLeft: ({ n }) =>
      count(n, "بلاصة فارغة", "زوز بلايص فارغين", "بلايص فارغين", "بلاصة فارغة"),
    noOnline: "ما فمّا حتى طرح يستنّى",
    noOnlineSub: "انزل على «ألعب» باش تبدا طرح جديد",
    param: {
      std: "خطيّة عاديّة",
      jokerHand: "جوكر في اليد",
      useful: "طيّش ورقة بلاس",
      continuer: "شدّ الفريش",
      nefrechFausse: "تفرش من كف صاحبك",
      pureSeq: "سويت من غير جوكر",
    },
    toast: {
      matchmaking: "ما نجّمناش نلقاولك طرح",
      create: "ما نجّمناش نبداو الطرح",
      quit: "خرجت من الطرح",
      solo: ({ n }) => `طرح ضدّ ${bots(n)} بدا 🤖`,
    },
  },

  // Tutorial (Lobby header). Wording proposed by Claude, not yet checked by the
  // owner; it reuses the glossary words (بيوشي، الكُدس، طيّش، تمنع، فرش…).
  tuto: {
    button: "كيفاش نلعب؟",
    title: "الرامي في دقيقة",
    next: "كمّل",
    skip: "فوّت",
    done: "فهمت!",
    close: "سكّر",
    hand: "يدّك",
    who: { leila: "ليلى", sami: "سامي" },
    goal: {
      title: "إلّي يكمّل الأوّل يربح",
      body: "كل لاعب ياخذ 14 ورقة، وكي يبيوشي يولّي عندو 15. باش تربح الجولة، نزّل <b>14 ورقة</b> في مجموعات من 3 أوراق على الأقل، ومبعد طيّش <b>الورقة الـ15</b>: هكّا <b>تفرش</b>.",
      tip: "تتلعب بـ2 ولّا بـ4 لاعبين، والطرح يتلعب على برشا جولات.",
      win: "فرشت!",
    },
    turn: {
      title: "دورك في 3 خطوات",
      // The two gestures: tap the draw pile ("انزل على", as elsewhere in the app), drag a card onto the discard pile.
      body: "<b>انزل على البيوش</b> باش تبيوشي، <b>نزّل</b> أوراقك كان تنجّم، ومبعد <b>جرّ</b> ورقة للكُدس باش يوفى دورك.",
      tip: "كان تهزّ آخر ورقة في الكُدس، لازمك تمنع بيها: شوف كيفاش تمنع في الخطوات الجاية.",
    },
    combos: {
      title: "المجموعات",
      body: "<b>سويت</b>: 3 أوراق ولّا أكثر ورا بعضهم، من نفس اللون. <b>بريلان</b>: 3 ولّا 4 أوراق نفس الرقم، كل وحدة من لون.",
      // Each Latin run sits between Arabic words: next to a letter ("K (Q-K-A)")
      // the bidi algorithm would glue them into one left-to-right chunk.
      tip: `الآص يجي في الأوّل (${run("A-2-3")}) ولّا في الآخر (${run("Q-K-A")}).`,
      seq: "سويت",
      set: "بريلان",
    },
    joker: {
      title: "الجوكر",
      body: "يعوّض <b>أي ورقة</b>. كي يكون على الطاولة، حطّ الورقة الصحيحة في بلاصتو باش تهز الجوكر. في السويت، <b>زوز جوكر ما يكونوش حذا بعضهم</b>.",
      tip: "جوكر يقعد في يدّك آخر الجولة يكلّفك 50 بونتو.",
    },
    // Keeps the players' words: تمنع (Nemna3) and تفرش (Nefrech).
    save: {
      title: "أوّل تمنع، ومبعد تفرش",
      body: "مادامك ما <b>منعتش</b>، الجولة تنجّم تكلّفك <b>100 بونتو</b>. كي تمنع، تحسب كان الأوراق إلّي في يدّك. ومبعد نزّلهم الكل وطيّش الأخيرة باش <b>تفرش</b> وتربح الجولة.",
      tip: "تنجّم زادة تنزّل الكل مرّة وحدة في نفس الدور: هكّا تفرش طول.",
      ifEnds: "كان الجولة توفى توّا",
      none: "ما منعتش",
      saved: "منعت",
      won: "فرشت!",
    },
    nemna3: {
      title: "كيفاش تمنع؟",
      body: "من الدور الثاني متاعك تنجّم تنزّل. أوّل مرّة، لازم توصل <b>المنعة</b> (مكتوبة الفوق)، وكان «القوانين» تطلب، يلزمك زادة <b>سويت من غير جوكر</b>. ومن بعدك، المنعة تطلع.",
      // Words rather than "K = 10": in RTL that equation reads backwards.
      tip: `البونتو: من 2 لـ 9 كل ورقة تسوى رقمها، و10 و J و Q و K كل وحدة تسوى 10. الآص يسوى 1 في ${run("A-2-3")}، و10 في غيرها. الجوكر يسوى الورقة إلّي يعوّضها.`,
      pure: "سويت من غير جوكر",
    },
    table: {
      title: "ومبعد: زيد على الطاولة",
      body: "كي تمنع، تنجّم تزيد أوراقك على <b>المجموعات الكل</b> إلّي على الطاولة، حتى متاع غيرك، وتهزّ من الكُدس كيف ما تحبّ.",
      warn: "ردّ بالك من «طيّش ورقة بلاس»: كي تطيّش ورقة تنجّم تتزاد على الطاولة، تاخذ 50 بونتو خطيّة.",
    },
    score: {
      title: "آخر الجولة",
      body: "إلّي فرش ياخذ 0. الباقين يحسبو الأوراق إلّي قعدو في يدّهم، وإلّي ما منعش ياخذ <b>100</b>. إلّي يوصل لحدّ الخروج يخرج برّا، وآخر واحد يقعد يربح الطرح. بـ4 لاعبين، آخر زوز يربحو، وإلّي عندو أقل بونتو هو الأوّل.",
      tip: "في أوّل كل جولة: كان الكل قالو «فريش»، نعاودو التفريق. وكي لاعب يقول «ألعب»، الجولة تبدا — وكان ما منعش، ياخذ 50 بونتو زيادة (شدّ الفريش).",
    },
    ready: {
      title: "هيّا، الدور عليك!",
      body: "باش تتعلّم خير: ألعب طرح ضدّ بوت مبتدي. القهواجي يقلّك شنوّة تعمل في كل دور.",
      bodyGame: "الطرح بدا! <b>القهواجي</b>، تحت على اليسار، يقلّك شنوّة تعمل في كل دور. انزل عليه باش تشوف <b>آش صار</b> في الطرح.",
      practice: "ألعب ضدّ بوت",
    },
  },

  room: {
    addBots: "زيد بوتات",
    role: { bot: "بوت", host: "مولى الطرح", player: "لاعب" },
    waitingSlot: "نستنّاو فيه…",
    start: "يلّا نبداو!",
    startWith: ({ n }) => `يلّا نبداو بـ${players(n)}`,
    waitingCount: "نستنّاو في الجماعة ({a}/{b})",
    waitingLaunch: "نستنّاو في مولى الطرح باش يبدا…",
    waitingTitle: "نستنّاو في اللاعبين",
    // The owner's words: keep them as written.
    shareHint: "أبعث الكود لأصحابك، ولّا زيد بوتات",
    codeLabel: "كود الطرح",
    // The same, in the chip of the top bar (a phone's width).
    codeShort: "كود الطرح",
    codeCopied: "الكود تنسخ",
    cantStart: "ما نجّمناش نبداو",
    cantLaunch: "ما نجّمناش نبداو",
    rematchNote: "نفس الجماعة، نفس القوانين",
    // Proposed by Claude, not yet confirmed by the owner.
    table: {
      title: "لون الطاولة",
      names: {
        "emeraude": "زمرّدي",
        "bleu-nuit": "أزرق ليلي",
        "bordeaux": "بوردو",
        "mediterranee": "بحري",
        "noir-or": "أكحل وذهبي",
        "neon": "نيون",
      },
    },
  },

  // Shared by both waiting rooms (components/room/WaitingTable.jsx and its users).
  // Proposed by Claude, not yet confirmed by the owner; the tips reuse the tutorial's words.
  waitingTable: {
    full: "الطاولة كملت!",
    missing: ({ n }) => `${sg(n, "ناقص", "ناقصين")} ${players(n)} ونبداو!`,
    hostStarts: "إبدأ الطرح وقت إلّي تحب",
    waitHost: "مولى الطرح باش يبدا",
    noRank: "مع البوتات برك، ما فماش نقاط رتبة",
    // Under the count in the middle of the table ("2/4").
    atTable: "على الطاولة",
    startsIn: "نبداو بعد",
    ready: "حاضر",
    starting: "نبداو…",
    tipTitle: "تعرفشي؟",
    // « Le savais-tu ? » (Tips in WaitingTable.jsx), in this order. They follow the room's rules:
    // {n} = the room's penalty; a tip whose rule is off in the room is not shown.
    tips: {
      joker: "الجوكر يعوّض أي ورقة. كي يكون على الطاولة، حطّ الورقة الصحيحة في بلاصتو باش تهز الجوكر.",
      noPose: "مادامك ما منعتش، الجولة تنجّم تكلّفك 100 بونتو. كي تمنع، تحسب كان الأوراق إلّي في يدّك.",
      ace: `الآص يجي في الأوّل (${run("A-2-3")}) ولّا في الآخر (${run("Q-K-A")}).`,
      usefulDiscard: "ردّ بالك من «طيّش ورقة بلاس»: كي تطيّش ورقة تنجّم تتزاد على الطاولة، تاخذ {n} بونتو خطيّة.",
      jokerHand: "جوكر يقعد في يدّك آخر الجولة يكلّفك {n} بونتو.",
      pureSeq: "باش تمنع أوّل مرّة، يلزمك سويت من غير جوكر.",
      threshold: "كل ما لاعب يمنع، المنعة تطلع: إلّي بعدو لازمو يمنع بأكثر.",
      takeDiscard: "مادامك ما منعتش، كان تهزّ آخر ورقة في الكُدس لازمك تمنع بيها.",
      addToTable: "كي تمنع، تنجّم تزيد أوراقك على المجموعات الكل إلّي على الطاولة، حتى متاع غيرك.",
      continuer: "في أوّل الجولة: كي لاعب يقول «ألعب»، الجولة تبدا — وكان ما منعش، ياخذ {n} بونتو زيادة (شدّ الفريش).",
      botReplace: "إلّي يطيح عليه الريزو، بوت ينجّم يلعب في بلاصتو: الطرح يكمّل.",
      jokersApart: "في السويت، زوز جوكر ما يكونوش حذا بعضهم: 5-6-7 بزوز جوكر تتحط جوكر-5-6-7-جوكر.",
    },
  },

  // Waiting room of « Jouer en ligne », with strangers (components/room/OnlineWaitingRoom.jsx).
  // Proposed by Claude, not yet confirmed by the owner.
  onlineRoom: {
    searching: "نلوّجو على لاعبين",
    startsSoon: "حضّر روحك، الطرح باش يبدا",
    seatSearching: "نلوّجو…",
    elapsed: "قدّاش استنّيت",
    cta: {
      waiting: "نستنّاو في اللاعبين ({a}/{b})",
      startNow: "إبدأ توّا",
      startsIn: ({ n }) => `نبداو بعد ${seconds(n)}`,
    },
    bots: {
      ask: "زيد بوتات باش تبدا فيسع",
      fill: "كمّل ببوتات",
    },
  },

  // Colours of the table locked behind a video (components/TableColorPicker.jsx).
  tableColor: {
    button: "الطاولة",
    title: "لون الطاولة",
    lockedLabel: "{name}: مسكّر، شوف فيديو باش تحلّو",
    hintApp: "الأخضر ديما محلول. شوف فيديو قصير باش تحلّ لون آخر.",
    hintWeb: "الأخضر ديما محلول. الألوان الأخرين يتحلّو في تطبيقة رامي أونلاين، بفيديو قصير.",
    appOnly: "اللون هذا يتحلّ في تطبيقة رامي أونلاين، بفيديو قصير.",
    unlocked: "{name} تحلّ",
    dismissed: "الفيديو تقصّ: شوفو للآخر باش يتحلّ اللون.",
    noVideo: "ما فماش فيديو توّا. عاود جرّب بعد شويّة.",
    saveError: "ما نجّمناش نسجّلو اللون. ثبّت في الكونكسيون متاعك.",
  },

  join: {
    invited: "راك مستدعي! 🎉",
    nameEnough: "اسمك برك ويزّي باش تلعب معاهم",
    gameOf: "الطرح",
    status: { started: "يتلعب توّا", full: "معبّي", open: "يستنّى فيك" },
    invalidLink: "اللينك غالط",
    invalidLinkDesc: "الطرح هذا تسكّر ولّا اللينك وفى.",
    connectingGame: "قاعدين ندخّلو فيك للطرح…",
    fullTitle: "الطرح معبّي",
    fullDesc: "اللاعبين الكل دخلو — الطرح باش يبدا توّا.",
    refresh: "عاود ثبّت",
    startedTitle: "الطرح بدا",
    startedDesc: "الطرح بدا من غيرك. إبدأ طرح وحدك وألعب.",
    cantJoin: "ما نجّمتش تدخل للطرح",
    googleSub: "النتايج متاعك تتحفظ من طرح لطرح",
  },

  game: {
    loading: "الطرح قاعد يحضر",
    // Proposed by Claude, not yet confirmed by the owner.
    timer: {
      hurry: "⏳ مازالو 10 ثواني برك!",
      bonus: "+{n} ث",
      secs: "{n} ث",
    },
    ws: {
      quit: "❌ {name} خرج من الطرح وما عادش راجع",
    },
    ev: {
      voteCast: "↺ {name} قال: فريش ({a}/{b})",
      voteCastNext: "↺ {name} قال: فريش ({a}/{b}) — الدور على {next}",
      continuer: "▶ {name} قال: ألعب",
      redealDone: "↺ فريش! أوراق جديدة للجميع.",
      redealCancelled: "الجولة تكمّل — ما فمّاش فريش ({names} طلبو فريش)",
    },
    turn: {
      draw: [
        "✋ دورك يا معلّم — بيوشي ولّا خوذ من الكُدس",
        "✋ جا دورك! هزّ ورقة من البيوش ولّا من الكُدس",
        "✋ هيّا عاد، الدور عليك — بيوشي ولّا خوذ من الكُدس",
        "✋ الدور عليك يا بطل — بيوشي!",
        "✋ هاو جا دورك… ربّي يجيبلك الورقة إلّي تستنّى فيها 🤞",
      ],
      // The discard pile cannot be taken (first turn, the previous player's
      // first discard, locked or empty): the draw pile only.
      drawOnly: [
        "✋ دورك يا معلّم — بيوشي من البيوش",
        "✋ جا دورك! هزّ ورقة من البيوش",
        "✋ هيّا عاد، الدور عليك — الكُدس مسكّر الدور هذا، بيوشي",
        "✋ الدور عليك يا بطل — بيوشي!",
        "✋ هاو جا دورك… ربّي يجيبلك الورقة إلّي تستنّى فيها 🤞",
      ],
      act: [
        "🃏 نزّل أوراقك ولّا طيّش ورقة في الكُدس",
        "🃏 توّا يا تمنع يا تطيّش… القرار عندك",
        "🃏 خمّم مليح: تنزّل ولّا تطيّش؟",
        "🃏 الورقة في يدّك… ورّينا شنوّة عندك",
        "🃏 تمنع، تفرش، ولّا تطيّش؟ ألعب!",
      ],
      // The player's first turn of the round: nothing can be posed yet.
      discardOnly: [
        "🃏 أوّل دور: ما تنجّمش تنزّل، طيّش ورقة برك",
        "🃏 أوّل دورك: طيّش ورقة في الكُدس",
        "🃏 ما فمّاش تنزيل في أوّل دور — طيّش وحدة",
        "🃏 خمّم مليح وطيّش ورقة في الكُدس",
        "🃏 أوّل دور… إختار ورقة وطيّشها",
      ],
    },
    // The waiter teases a player who lets the turn sit idle (see Game.jsx).
    nudge: {
      draw: [
        "⏳ يا معلّم، رقدت؟ بيوشي عاد! 😴",
        "⏳ الجماعة تستنّى فيك… هزّ ورقة!",
        "⏳ القهوة بردت وإنت مازلت تخمّم ☕",
        "⏳ هيّا يا بطل، بيوشي ولّا خوذ من الكُدس",
        "⏳ الدور عندك من بكري… ألعب!",
        "⏳ ما تخافش، الورقة ما تعضّش 😄 بيوشي!",
        "⏳ أصحابك شابو وهوما يستنّاو 👴",
        "⏳ يزّي من التخمام، مدّ يدّك للبيوش",
        "⏳ آلو؟ فمّا شكون؟ الدور عليك! 📞",
        "⏳ البيوش ما يهربش، أمّا أصحابك يملّو 😅",
        "⏳ الورقة الباهية تستنّى فيك في البيوش 😉",
        "⏳ يا خويا، الدور دورك… بيوشي وريّحنا!",
        "⏳ التاي برد والجماعة قلقت 🍵",
        "⏳ تخمّم في شنوّة؟ مازلت ما هزّيتش ورقة! 😄",
        "⏳ هيّا، هزّ ورقة قبل ما يوفى الليل 🌙",
        "⏳ الماتش وفى وإنت مازلت ما بيوشيتش ⚽",
        "⏳ صاحبك قاعد يتثاوب… بيوشي عاد! 🥱",
        "⏳ حتى القهواجي يستنّى فيك 😅",
        "⏳ الدور ما يتعدّاش وحدو، هزّ ورقة!",
        "⏳ بيوشي ولّا خوذ من الكُدس… المهمّ ألعب!",
        "⏳ البيوش ولّا الكُدس، المهمّ اختار! 😄",
        "⏳ البيوش يغمزلك… 😉",
        "⏳ الطاولة الكلّ حابسة نفسها 😮",
        "⏳ ورقة صغيرة باش نبداو؟",
        "⏳ القهواجي يدقدق على الطاولة… الدور عليك! 🃏",
        "⏳ حتى الجوكر قلق 🤡",
        "⏳ عندنا الوقت نطلبو قهوة أخرى ☕☕",
        "⏳ الطرح ما يتلعبش من غيرك!",
        "⏳ يدّك تستنّى في الورقة الخمسطاش 🖐️",
        "⏳ ورقة وحدة وانطلق!",
      ],
      drawOnly: [
        "⏳ يا معلّم، رقدت؟ بيوشي عاد! 😴",
        "⏳ الجماعة تستنّى فيك… هزّ ورقة من البيوش!",
        "⏳ القهوة بردت وإنت مازلت تخمّم ☕",
        "⏳ هيّا يا بطل، الكُدس مسكّر الدور هذا… بيوشي!",
        "⏳ الدور عندك من بكري… بيوشي!",
        "⏳ ما تخافش، الورقة ما تعضّش 😄 بيوشي!",
        "⏳ أصحابك شابو وهوما يستنّاو 👴",
        "⏳ يزّي من التخمام، مدّ يدّك للبيوش",
        "⏳ آلو؟ فمّا شكون؟ الدور عليك! 📞",
        "⏳ البيوش ما يهربش، أمّا أصحابك يملّو 😅",
        "⏳ الورقة الباهية تستنّى فيك في البيوش 😉",
        "⏳ يا خويا، الدور دورك… بيوشي وريّحنا!",
        "⏳ التاي برد والجماعة قلقت 🍵",
        "⏳ تخمّم في شنوّة؟ مازلت ما هزّيتش ورقة! 😄",
        "⏳ هيّا، هزّ ورقة قبل ما يوفى الليل 🌙",
        "⏳ الماتش وفى وإنت مازلت ما بيوشيتش ⚽",
        "⏳ صاحبك قاعد يتثاوب… بيوشي عاد! 🥱",
        "⏳ حتى القهواجي يستنّى فيك 😅",
        "⏳ الدور ما يتعدّاش وحدو، هزّ ورقة!",
        "⏳ الكُدس مسكّر، ما عندك كان البيوش… هيّا! 😄",
        "⏳ البيوش يغمزلك… 😉",
        "⏳ الطاولة الكلّ حابسة نفسها 😮",
        "⏳ ورقة صغيرة باش نبداو؟",
        "⏳ القهواجي يدقدق على الطاولة… الدور عليك! 🃏",
        "⏳ حتى الجوكر قلق 🤡",
        "⏳ عندنا الوقت نطلبو قهوة أخرى ☕☕",
        "⏳ الطرح ما يتلعبش من غيرك!",
        "⏳ يدّك تستنّى في الورقة الخمسطاش 🖐️",
        "⏳ ورقة من البيوش وانطلق!",
        "⏳ الدور هذا البيوش برك… هيّا! 🃏",
      ],
      act: [
        "⏳ طيّش ورقة وريّحنا! 😅",
        "⏳ يا معلّم، الجماعة تستنّى… تنزّل ولّا تطيّش؟",
        "⏳ خمّمت برشا، توّا ألعب!",
        "⏳ القهواجي جاب القهوة وإنت مازلت تخمّم ☕",
        "⏳ هيّا عاد، طيّش وحدة وكمّل دورك",
        "⏳ الأوراق ما يتبدّلوش وحدهم 😄 ألعب!",
        "⏳ أصحابك باش يرقدو… طيّش ورقة! 😴",
        "⏳ عندك منعة؟ نزّلها. ما عندكش؟ طيّش!",
        "⏳ موش شطرنج هذا يا خويا، رامي! ألعب ♟️",
        "⏳ الليل طويل أمّا الجولة لا… هيّا!",
        "⏳ الورقة في اليد تسخن… طيّشها! 🔥",
        "⏳ يا معلّم، نزّل ولّا طيّش… المهمّ تحرّك! 😄",
        "⏳ التاي برد وإنت مازلت تحسب 🍵",
        "⏳ كان عندك سويت نزّلها، ما تخبّيهاش! 😏",
        "⏳ الجماعة باش تطلب قهوة أخرى… طيّش! ☕",
        "⏳ حسبتهم مليح؟ هيّا طيّش وحدة 😅",
        "⏳ ما فمّاش ورقة مثالية… طيّش وتوكّل! 🤲",
        "⏳ الجوكر ما يجيش بالتخمام 😄 ألعب!",
        "⏳ صاحبك باش يشيب وهو يستنّى… طيّش! 👴",
        "⏳ الوقت يجري والأوراق في بلاصتهم… هيّا! ⏰",
        "⏳ نزّل ولّا طيّش، الطاولة قلقت 😄",
        "⏳ الطاولة الكلّ تحسب معاك في أوراقك 🧮",
        "⏳ القهواجي بدا يتثاوب 🥱",
        "⏳ قرار واحد: أما ورقة باش تطيّش؟ 🤔",
        "⏳ اللي معاك يحضّرو في الروفانش 😏",
        "⏳ الأوراق على الطاولة خير من اليد 😉",
        "⏳ خلّينالك بلاصة… أمّا موش الليل الكلّ 🌙",
        "⏳ حتى الجوكر اختار، وإنت؟ 🤡",
        "⏳ تنفّس، اختار، طيّش! 😌",
        "⏳ صاحبك كمّل قهوتو ☕",
      ],
      // First turn: discarding is the only move — never invite to pose.
      discardOnly: [
        "⏳ طيّش ورقة وريّحنا! 😅",
        "⏳ خمّمت برشا، توّا ألعب!",
        "⏳ القهواجي جاب القهوة وإنت مازلت تخمّم ☕",
        "⏳ هيّا عاد، طيّش وحدة وكمّل دورك",
        "⏳ الأوراق ما يتبدّلوش وحدهم 😄 ألعب!",
        "⏳ أصحابك باش يرقدو… طيّش ورقة! 😴",
        "⏳ موش شطرنج هذا يا خويا، رامي! ألعب ♟️",
        "⏳ الليل طويل أمّا الجولة لا… هيّا!",
        "⏳ الورقة في اليد تسخن… طيّشها! 🔥",
        "⏳ التاي برد وإنت مازلت تحسب 🍵",
        "⏳ الجماعة باش تطلب قهوة أخرى… طيّش! ☕",
        "⏳ حسبتهم مليح؟ هيّا طيّش وحدة 😅",
        "⏳ ما فمّاش ورقة مثالية… طيّش وتوكّل! 🤲",
        "⏳ الجوكر ما يجيش بالتخمام 😄 ألعب!",
        "⏳ صاحبك باش يشيب وهو يستنّى… طيّش! 👴",
        "⏳ الوقت يجري والأوراق في بلاصتهم… هيّا! ⏰",
        "⏳ أوّل دور ما فيهش تنزيل… طيّش وحدة وهيّا! 😄",
        "⏳ حاجة وحدة تعملها: طيّش ورقة! 🃏",
        "⏳ الطاولة الكلّ تحسب معاك في أوراقك 🧮",
        "⏳ القهواجي بدا يتثاوب 🥱",
        "⏳ قرار واحد: أما ورقة باش تطيّش؟ 🤔",
        "⏳ اللي معاك يحضّرو في الروفانش 😏",
        "⏳ خلّينالك بلاصة… أمّا موش الليل الكلّ 🌙",
        "⏳ تنفّس، اختار، طيّش! 😌",
        "⏳ صاحبك كمّل قهوتو ☕",
        "⏳ عندك ورقة زايدة في يدّك… أما وحدة؟ 🃏",
        "⏳ طيّش والجولة تنطلق! 🚀",
        "⏳ اختار الورقة اللي ما تلزمكش وباي باي! 👋",
      ],
    },
    hint: {
      draw: "بيوشي ولّا خوذ من الكُدس",
      drawOnly: "هز ورقة من البيوش",
      pending: ({ n }) => `${groups(n)} · طيّش ورقة باش تمنع`,
      act: "طيّش ورقة ولّا نزّل أوراقك",
      discardOnly: "أوّل دور: طيّش ورقة",
      lastCard: "طيّش آخر ورقة باش تربح الجولة",
      belowThreshold: ({ n, missing }) => `${groups(n)} · ناقصك ${missing} بونتو باش تمنع`,
      needPureSeq: ({ n }) => `${groups(n)} · لازمك سويت من غير جوكر باش تمنع`,
    },
    err: {
      select3: "اختار 3 أوراق على الأقل",
      invalidCombo: "الأوراق هاذم ما يركبوش مع بعضهم",
      validation: "ما نجّمناش نثبّتو، عاود جرّب",
      max14Cancelled:
        "ما تنجّمش تنزّل أكثر من 14 ورقة في الدور. المجموعة هاذي تبطّلت (جرّبت {n}).",
      max14: "ما تنجّمش تنزّل أكثر من 14 ورقة في الدور (جرّبت {n}).",
      keepOne: "خلّي ورقة على الأقل في يدّك باش تطيّشها.",
      fausseEmpty: "الكُدس فارغ",
      notYourTurn: "موش دورك، استنّى شويّة 😉",
      alreadyDrewTurn: "ياخي هزّيت ورقة في الدور هذا",
      alreadyDrew: "هزّيت ورقة ديجا",
      discardLocked: "الكُدس مسكّر الدور هذا (إلّي قبلك خرج). بيوشي من البيوش.",
      discardClosed: "الكُدس مسكّر الدور هذا. بيوشي من البيوش.",
      oneCardDiscard: "ورقة وحدة برك تتطيّش في المرّة",
      drawFirst: "هزّ ورقة قبل، ومبعد طيّش",
      jokerPosUnknown: "ما عرفناش وين نحطّو الجوكر.",
      jokerNoSlot: "الجوكر ما عندوش بلاصة في السويت هاذي.",
      jokerNoSlotBothEnds: "الجوكر ما عندوش بلاصة في السويت هاذي (لا قبل لا بعد).",
      jokersSideBySide: "زوز جوكر ما ينجموش يكونو حذا بعضهم في السويت.",
      jokerCantAdd: "الجوكر ما يدخلش في السويت هاذي.",
      invalidWithCards: "ما تركبش بالأوراق هاذم",
      cantAddNow: "ما تنجّمش تزيد توّا",
      groupFull: "المجموعة هاذي كاملة (4 أوراق) — ما عادش تزيد عليها",
      poseFirstForJoker: "امنع قبل (≥ {n} بونتو) باش تنجّم تهزّ الجوكر هذا",
      pureSeqForJoker: "لازمك سويت من غير جوكر في مجموعاتك باش تنجّم تهزّ الجوكر هذا",
      replaceImpossible2: "الزوز أوراق هاذم ما يعوّضوش الجوكر",
      jokerInPendingGroup: "الجوكر هذا في مجموعة قاعد تحضّر فيها: نحّيها قبل ما تبطّل.",
      jokerInvalidSeq: "الجوكر ما يجيش في السويت هاذي",
      additionInvalid: "الورقة هاذي ما تتزادش هنا",
      neitherReplaceNorExtend: "الورقة هاذي لا تعوّض الجوكر لا تطوّل السويت.",
      cantPoseNow: "ما تنجّمش تنزّل توّا",
      min3: "المجموعة يلزمها 3 أوراق على الأقل",
    },
    ok: {
      cardTaken: "هزّيت الورقة",
      stagedTaken: "هزّيتها — توّا لازم تمنع بيها",
      cancelled: "بطّلت",
      nemna3CancelledPenalty: "المنعة تبطّلت — +{n} بونتو خطيّة 🫣",
      jokerUndo: "↩ رجّعت الجوكر لبلاصتو",
      modsUndone: "↩ رجّعنا كل شي كيما كان",
      jokerLeft: "الجوكر تحطّ على اليسار",
      jokerRight: "الجوكر تحطّ على اليمين",
      jokerLeftAuto: "الجوكر تحطّ على اليسار (وحدو)",
      jokerRightAuto: "الجوكر تحطّ على اليمين (وحدو)",
      jokerLeftPending: "الجوكر على اليسار (يستنّى)",
      jokerRightPending: "الجوكر على اليمين (يستنّى)",
      jokerRecoveredReal: "الجوكر رجعلك — الورقة الصحيحة خذات بلاصتو",
      jokerRecovered: [
        "♻️ الجوكر رجعلك! 😎",
        "♻️ صحّة ليك، الجوكر في يدّك!",
        "♻️ خطفت الجوكر! 🃏",
      ],
      addedToGroup: ({ n }) => `${cards(n)} ${sg(n, "تزادت", "تزادو")} للمجموعة ✓`,
      pendingAdd: ({ n }) => `${cards(n)} ${sg(n, "تستنّى باش تتزاد", "يستنّاو باش يتزادو")}`,
      cardsAdded: ({ n }) => `${cards(n)} ${sg(n, "تزادت", "تزادو")} ✓`,
      autoNefrech: "🏆 فرشت! 🔥",
    },
  },

  top: {
    paramsTitle: "قوانين الطرح",
    thresholdInit: "المنعة الأولى",
    elimScore: "حدّ الخروج",
    pureSeqReq: "سويت من غير جوكر",
    penStd: "خطيّة عاديّة",
    jokerHand: "جوكر في اليد",
    usefulDiscard: "طيّش ورقة بلاس",
    continuer: "شدّ الفريش",
    nefrechFausse: "تفرش من كف صاحبك",
    disabled: "مسكّرة",
    round: "الجولة {n}",
    threshold: "المنعة: {n}",
    infos: "القوانين",
    soundOn: "حلّ صوت اللاعبين",
    soundOff: "سكّر صوت اللاعبين",
    music: "الموسيقى",
    musicOn: "حلّ الموسيقى",
    musicOff: "سكّر الموسيقى",
    moods: {
      emeraude: "جاز القهوة",
      "bleu-nuit": "ليلة نجوم",
      bordeaux: "صالون",
      neon: "سينث ويف",
      "noir-or": "كازينو الليل",
      mediterranee: "أندلسي",
    },
  },

  pioche: {
    draw: "بيوشي",
    discard: "طيّش",
    pile: "البيوش",
    pileCount: "البيوش ({n})",
    take: "هزّ",
    closed: "مسكّر",
    discardLabel: "الكُدس",
    fausse: "الكُدس",
    fausseCount: "الكُدس ({n})",
    taken: "تهزّت",
    drag: "جرّ",
    dropHere: "طيش هنا",
    empty: "فارغ",
    cancel: "أرجع",
  },

  table: {
    // Combo tags stay in French (owner's choice): players read "BR" / "SU".
    set: "Br",
    seq: "Su",
    preparing: "⏳ {name} يخمّم…",
    cancelAdd: "نحّي الزيادة",
    swap: "♻ تبديل",
    addedAndJoker: "أوراق تزادو + جوكر تهز",
    nAdded: ({ n }) => `${cards(n)} ${sg(n, "تزادت", "تزادو")}`,
    jokerRecovered: "جوكر تهز",
    modsPending: "تبديلات مازالت",
    undo: "أرجع",
    drop3: "جرّ 3 أوراق ولّا أكثر",
  },

  bottom: {
    yourTurn: "دورك",
    continuer: "ألعب",
    relancer: "مازال ما منعش",
    nCards: ({ n }) => cards(n),
    sortBy: "رتّب",
    sortValue: "رقم",
    sortSuit: "لون",
    emoji: "أبعث إيموجي",
  },

  feed: {
    aria: "آش صار في الطرح",
    dealer: "القهواجي",
    title: "القهواجي · آش صار",
    empty: "مازال ما صار شي… ☕",
  },

  // Proposed by Claude, not yet confirmed by the owner.
  profile: {
    title: "البروفيل متاعي",
    name: "إسمك",
    nameHint: "هذا الإسم إلّي يشوفوه اللاعبين في الطاولة.",
    avatar: "الصورة متاعك",
    googlePhoto: "تصويرة Google",
    initial: "الحرف الأوّل",
    save: "سجّل",
    saved: "البروفيل تسجّل ✓",
    close: "سكّر",
    google: "حساب Google",
    guest: "حساب ضيف",
    guestNote: "", // no note in Tunisian (owner's choice)
    saveError: "ما نجّمناش نسجّلو البروفيل. عاود بعد شوية.",
    card: "بطاقة اللاعب متاعك",
    record: ({ g, w, l }) => `${g} طرح · ${w} ربح · ${l} خسارة`,
    account: "الحساب",
    avatars: {
      wolf: "ذيب", goldlion: "سبع ذهبي", eagle: "نسر", cobra: "كوبرا", scorpion: "عقرب", skull: "جمجمة",
      nvg: "كوموندو", reticle: "منظار", ace: "آص بجناحين", king: "ملك", joker: "جوكر", rank: "رتبة",
    },
  },

  // Proposed by Claude, not yet confirmed by the owner.
  rank: {
    title: "الرتبة",
    tabs: { me: "رتبتي", board: "الترتيب", recent: "آخر اللاعبين" },
    // Maître = ماستر: « معلّم » is the winner's title (winner.champion), « أسطورة » the Légende bots.
    tiers: { bronze: "برونز", argent: "فضّة", or: "ذهب", platine: "بلاتين", diamant: "ماس", maitre: "ماستر" },
    pr: "{n} نقطة",
    gain: "+{n} نقطة",
    drop: "−{n} نقطة",
    calibrating: "تجربة",
    calibrationCount: "{n}/{total} أطراح",
    calibrationNote: "أوّل {n} أطراح مع لاعبين آخرين يتحسبو مرّتين. من بعدهم تبان رتبتك.",
    toNext: "{n} نقطة و توصل {rank}",
    top: "أعلى رتبة",
    stats: {
      games: "أطراح",
      wins: "ربح",
      losses: "خسارة",
      winRate: "نسبة الربح",
      streak: "ربح ورا بعضو",
      bestStreak: "أحسن سلسلة",
      roundsWon: "جولات ربحتهم",
      abandons: "خرجات",
      peak: "أحسن رتبة",
    },
    last: "آخر طرح",
    ladder: "الرتب",
    how: {
      title: "كيفاش تخدم؟",
      place: "الطرح تربح ولّا تخسر فيه نقاط رتبة، على حساب بلاصتك في الطاولة.",
      humans: "يتحسب كي يبداوه زوز لاعبين حقيقيين على الأقل، حتّى كان تكمّل مع البوتات. قدّام البوتات وحدهم: لا نقاط ولا أرقام.",
      winners: "بـ4 لاعبين، الطرح فيه زوز رابحين، مرتّبين على حساب البونتو: الأوّل ياخو نقاط أكثر من الثاني.",
      strength: "كي تغلب واحد أقوى منك تربح أكثر، و كي تخسر قدّام واحد أضعف تخسر أكثر. البوتات إلّي في الطاولة يتحسبو على حسب مستواهم.",
      kept: "الرتبة إلّي توصلها تبقى متاعك: عمرك ما تهبط تحتها.",
      quit: "كي تخرج من طرح مازال ماشي تتحسب خسارة، و 10 نقاط زيادة.",
    },
    guestNote: "حساب ضيف: الرتبة متاعك تتحفظ في الجهاز هذا. أدخل بـ Google باش تلقاها في أي جهاز.",
    board: {
      you: "إنت",
      position: "بلاصتك: {n}",
      unranked: "كمّل التجربة باش تدخل للترتيب.",
      empty: "حتّى حد مازال ما ترتّب. الدور عليك!",
      error: "الترتيب موش متوفّر توّا.",
    },
    card: { record: "{w} ربح · {l} خسارة", open: "شوف رتبتي" },
    change: {
      win: "ربحت",
      winSecond: "ربح · الثاني",
      loss: "{place} من {of}",
      abandon: "خرجت",
      promoted: "طلعت!",
      newTier: "رتبة جديدة!",
      placed: "رتبتك",
      calibration: "تجربة {n}/{total}",
    },
  },

  // Proposed by Claude, not yet confirmed by the owner (invite = إستدعى, invited = مستدعي, Lobby = القهوة).
  players: {
    title: "بروفيل اللاعب",
    status: { online: "كونيكتي", in_game: "يلعب توّا", offline: "موش كونيكتي" },
    guest: "ضيف",
    you: "هذا إنت!",
    invite: "إستدعيه يلعب",
    invited: "الإستدعاء مشى ✓",
    waitNote: "الإستدعاء يستنّاه 10 دقايق في القهوة.",
    finishGame: "كمّل الطرح متاعك باش تستدعيه.",
    sameTable: "راهو معاك في الطاولة.",
    notFound: "اللاعب هذا ما عادش موجود.",
    error: "صارت مشكلة، عاود بعد شوية.",
    recentEmpty: "اللاعبين إلّي تلعب معاهم يبانو هوني.",
    open: "شوف بروفيل {name}",
  },

  invite: {
    title: "{name} يستدعي فيك تلعب",
    sub: "طاولة مع الأصحاب تستنّى فيك",
    expires: "مازال {n} دق",
    join: "أدخل",
    decline: "لا، شكرا",
    quitFirst: "أخرج من الطرح متاعك قبل.",
    error: "الإستدعاء هذا ما عادش صالح.",
    more: ({ n }) => (n === 1 ? "+1 إستدعاء آخر" : `+${n} إستدعاءات أخرى`),
    seatPending: "مستدعي",
    seatDeclined: "رفض",
  },

  // Proposed by Claude, not yet confirmed by the owner.
  nav: { label: "القائمة", play: "ألعب", rank: "الرتبة", social: "الأصحاب", table: "الطاولة", profile: "البروفيل" },
  settings: {
    title: "البارامترات",
    sound: "الأصوات",
    tutorial: "عاود شوف كيفاش نلعب",
    privacy: "الخصوصية",
    logout: "أخرج من الحساب",
  },
  social: {
    invites: "الإستدعاءات",
    noInvites: "حتى إستدعاء توّا. كي يستدعيك لاعب، يبان هوني.",
    recent: "آخر اللاعبين",
    inviteHint: "إستدعي لاعب: طاولتك تستنّاه 10 دقايق في القهوة.",
    invite: "إستدعي",
    sent: "مشى",
  },

  account: {
    delete: "فسّخ حسابي",
    deleteTitle: "تحب تفسّخ حسابك؟",
    deleteBody: "حسابك، إسمك والطاولات اللي حلّيتهم باش يتفسخو نهائيا. كان إنت في طرح، تخرج منو. ما تنجمش ترجع فيها.",
    deleteConfirm: "فسّخ نهائيا",
    deleteError: "ما نجمناش نفسخو الحساب. عاود بعد شوية.",
  },

  redeal: {
    title: "فريش؟",
    question: "عجبوك أوراقك؟",
    lookHand: "ثبّت مليح في أوراقك قبل ما تختار",
    rejouer: "فريش",
    rejouerHint: "أوراق جديدة",
    continuer: "ألعب",
    continuerHint: "نلعب بأوراقي",
    stake: "كي تختار ألعب وما تمنعش: +{n} بونتو (شدّ الفريش)",
    thinking: "{name} يخمّم…",
    youVoted: "قلت فريش",
    turnSoon: "توّا يجيك الدور",
    chose: "{name} اختار يلعب",
    ok: "باهي",
  },

  summary: {
    gameOver: "🏆 الطرح وفى",
    roundOver: "الجولة {n} وفات",
    gameWin: "{name} ربح الطرح! 👑",
    roundWin: "{name} <b>فرش</b> وربح الجولة! 🔥",
    nefrechBadge: "فرش ✓",
    notPosed: "ما منعش",
    winningCombos: "الأوراق إلّي ربح بيهم",
    kind: { sequence: "سويت", set: "بريلان / كاري" },
    discarded: "الورقة إلّي طيّشها",
    posedCombos: "الأوراق إلّي نزّلهم",
    remaining: "الأوراق إلّي قعدو في اليد ({n})",
    handBefore: "الأوراق إلّي كانو في اليد قبل ما يفرش ({n})",
    nextRound: "الجولة الجاية",
    waitHost: "الجولة الجاية: نستنّاو في {name}",
    host: "مولى الطرح",
    noWinner: "حتّى حد ما ربح الجولة هاذي",
    backLobby: "أرجع للقهوة ☕",
  },

  winner: {
    funny: [
      "ما ربحتش طرح… عطيتهم درس خصوصي. 🃏",
      "معلّم وولد معلّم! اليوم القهوة على حسابهم. ☕",
      "قالولك الرامي حظّ… وريتهم إلّي هو صنعة. 😏",
      "حتى البوتات وقفو يصفّقولك. 🤖👏",
      "لعبت كيما يلعب معلّم في قهوة الحومة — وهوما يتفرّجو. 👑",
      "أصحابك توّا يلوّجو على عذر… وما لقاوش. 😅",
      "فرشت وخلّيتهم يحسبو في البونتوات. 🧮",
      "اليوم إنت الريّس. غدوة يطلبو الروفانش — ونفس النتيجة. 😈",
      "الورقة تحبّك، وإنت تعرف كيفاش تدلّلها. ✨",
      "برافو عليك! حتى الجوكر قال: نحبّ نلعب معاه. 🃏",
      "يزّيهم من اللعب، خلّيهم يتعلّمو منّك شويّة. 🎓",
      "ربحت بالراحة… كأنّك تشرب في كابوسان. ☕😎",
      "هوما جابو الحظّ، وإنت جبت المخّ. 🧠",
      "من اليوم نسمّيوها: «رامي متاعك». 📝",
      "خسّرتهم وهوما يضحكو… هاذي هي الصنعة. 😂",
      "طيّشتهم واحد واحد كيما تطيّش في ورقة زايدة. 🎴",
      "ما شافوكش جاي… وهذا هو الستيل. 😎",
      "مبروك! القهوة كلّها تحكي عليك الليلة. 🏛️",
      "الطرح متاعك، والورقة كانت تعرف من الأوّل. 🎴",
      "ربحة نظيفة، بلاش حتى خطيّة… تبارك الله عليك. 👏",
      "إلّي يلعب معاك يخسر، وإلّي ما يلعبش يندم. 🔥",
      "كمّل هكّا وتولّي تعطي في دروس بالفلوس. 💰",
      "قالو: نعاودو؟ قلتلهم: حاضر، وزيد نربح. 😈",
      "حتى عزّوزة الحومة سمعت بيك: «يا وليدي شكون علّمك؟» 👵",
      "اليوم ما لعبتش ورقة… الورقة هي إلّي لعبت في يدّيك. 🎴",
      "الحظّ يجي ويمشي، أمّا المعلّم يقعد معلّم. 👑",
      "ربحت وما عرقتش… قولّهم يجيبولك قازوزة. 🥤",
      "هوما مازالو يحسبو، وإنت ديجا في الطرح الجاي. 🚀",
      "صحّة ليك يا بطل! الليلة ترقد مرتاح. 😴🏆",
      "كيما يقول المثل: إلّي يفرش الأخّر، يضحك برشا. 😄",
    ],
    champion: "معلّم!",
    subtitle: "معلّم الرامي",
    second: "البلاصة الثانية!",
    secondSubtitle: "ربحت الطرح زادة",
    secondLine: "زوز بلايص في البوديوم، وحدة منهم متاعك. المرّة الجاية، الفوقانية!",
    ranking: "الترتيب النهائي",
    eliminated: "برّا",
    closing: "قاعدين نسكّرو…",
    backLobby: "↩ أرجع للقهوة",
  },

  // Arrows aren't mirrored by the bidi algorithm: the one that must sit on the
  // visual left comes last in an RTL string, and vice-versa.
  joker: {
    title: "وين نحطّو الجوكر؟",
    left: "يسار ←",
    right: "→ يمين",
  },

  opp: {
    replaceBot: "عوّضو ببوت",
    // No tag in Tunisian (owner's choice): the name "Bot 2" already says it.
    bot: [],
    offline: "خرج",
    away: "غايب",
    awayTitle: "خلّى الوقت يوفى: الأدوار متاعو قصار حتى يرجع",
    watchingAd: "إشهار",
    watchingAdTitle: "يتفرّج في إشهار باش يحلّ لون الطاولة",
    // Under the name of the player who said «ألعب» at the redeal vote (the penalty's own words).
    continuer: "شدّ الفريش",
    continuerTitle: "قال ألعب في عوض فريش: كان ما منعش قبل ما توفى الجولة، ياخذ خطيّة",
    // Keeps the {n} placeholder: OpponentDisplay renders the number in bold.
    cards: ({ n }) => count(n, "ورقة وحدة", "زوز أوراق", "أوراق", "ورقة").replace(/^\d+/, "{n}"),
    fausse: "+الكُدس",
    elimSuffix: " · برّا",
    nefrechInProgress: "⚡ {name} قاعد يفرش…",
    preparing: "🃏 {name} قاعد يحضّر باش يمنع…",
    addsExisting: ({ n }) =>
      `${cards(n)} ${sg(n, "زايدة على مجموعة محطوطة", "زايدين على مجموعات محطوطة")}`,
  },

  score: { title: "السكور" },

  voice: {
    unsupported: "الميكرو ما يخدمش في النافيقاتور هذا",
    insecure: "الميكرو ما ينجّمش يخدم: الكونيكسيون موش مأمّنة (لازم HTTPS)",
    on: "الميكرو تحلّ 🎙️",
    denied: "ما عطيتناش الإذن. حلّ الميكرو من إعدادات النافيقاتور.",
    notFound: "ما لقينا حتى ميكرو في الجهاز هذا.",
    busy: "الميكرو مشغول مع أبليكاسيون أخرى.",
    inaccessible: "ما نجّمناش نوصلو للميكرو ({reason})",
    unknown: "مشكل موش معروف",
    off: "الميكرو تسكّر",
    enable: "حلّ الميكرو",
    disable: "سكّر الميكرو",
    lost: "الميكرو تقطع. أنزل على الإيكون باش ترجّعو.",
    held: "الميكرو واقف (التليفون قاعد يستعمل فيه)",
    linkConnecting: "قاعدين نربطو الصوت…",
    failNetwork: "الصوت ما ينجّمش يوصل مع {name}: الريزو متاعكم مسكّر الكونيكسيون.",
    failSilent: "الصوت ما ينجّمش يوصل مع {name}: التليفون متاعو ما يجاوبش. لازم يعاود يحلّ الباج.",
    failOutdated: "الصوت ما ينجّمش يوصل مع {name}: الباج متاعو قديمة. لازم يعاود يحلّها.",
    details: "التفاصيل",
    reportTitle: "تشخيص الصوت",
    reportHint: "رابور تقني على ربط الصوت. انسخو وابعثو للسوبور.",
    refresh: "عاود",
    copy: "انسخ",
    copied: "الرابور تنسخ",
  },

  notif: { yourTurn: "جا دورك يا معلّم! (الجولة {n})" },

  // ── Backend messages ─────────────────────────────────────────────────────
  // The server writes its log and errors in French; lib/i18n/server.js matches
  // them and renders these templates instead. Arrays = banter variants, picked
  // per message so a given log line never changes wording.
  srv: {
    joker: "جوكر",
    listSep: "، ",
    kindSeq: "سويت",
    kindSet: "بريلان",
    thresholdNote: " — 🆙 المنعة ولّات {n} بونتو",
    nCards: ({ n }) => cards(n),

    roomOf: "طرح {name}",
    roomOnline: "طرح أونلاين",
    roomSolo: "{name} ضدّ البوتات",

    reconnected: "🟢 {name} رجع للطرح.",
    disconnected: "📡 {name} طاح عليه الريزو.",
    gameStarted: "الطرح بدا. {name} يبدا بـ15 ورقة. يلّا! 🎴",
    roundStarted: "الجولة {n} — {name} يبدا.",
    reshuffled: "البيوش وفى — خلّطنا الكُدس ورجّعناه للبيوش.",
    tookDiscard: ["{name} هزّ {card} من الكُدس.", "{name} عجبتو {card}، هزّها من الكُدس. 👀"],
    tookAndPosed: "🃏 {name} هزّ من الكُدس ومنع بـ {combos} ({pts} بونتو){note}.",
    posed: [
      "🃏 {name} نزّل {combos} ({pts} بونتو){note}.",
      "🃏 {name} منع! {combos} ({pts} بونتو){note}.",
    ],
    discarded: "{name} طيّش ورقة.",
    setBroken: "📤 بريلان {name} كمل: تكسّر، و{n} أوراق مشاو تحت الكُدس.",
    tookWithoutPosing:
      "⚠️ {name} هزّ من الكُدس وما منعش بيها (+{n} بونتو خطيّة). الورقة رجعت.",
    usefulDiscard:
      "⚠️ {name} طيّش {card} وهي تنجّم تتلعب على {kind} متاع {owner}. الورقة رجعتلو، +{n} بونتو خطيّة.",
    added: "➕ {name} زاد {cards} على {kind} متاع {target}.",
    jokerRecovered: "♻️ {name} خطف جوكر (من مجموعة {target}) بـ {card}.",
    jokerRecovered2: "♻️ {name} خطف جوكر (من مجموعة {target}) بـ {card} + {extra}.",
    nefrech: [
      "🏆 {name} فرش! الجولة وفات. 🔥",
      "🏆 {name} فرشها وسكّر الجولة! 👏",
      "🏆 يا سلام! {name} فرش والجولة وفات.",
    ],
    nefrechJokers: ({ n }) =>
      n === 1
        ? "🏆 {name} فرش وجوكر مازال في اليد! الجولة وفات. 😎"
        : "🏆 {name} فرش و{n} جوكرات مازالو في اليد! الجولة وفات. 😎",
    nefrechFausse: "⚠️ {name} فرش بورقة طيّشها {victim} ← +{n} بونتو على {victim}.",
    badJokerSwap: "⚠️ {name} جرّب يعوّض جوكر وما جاتش (+{n} بونتو خطيّة).",
    undoJoker: "↩️ {name} رجّع الجوكر لبلاصتو (مجموعة {target}).",
    undoAdd: "↩️ {name} بطّل الزيادة على مجموعة {target}.",
    undoAll: "↩️ {name} بطّل التبديلات الكل على مجموعة {target}.",
    eliminatedMidRound: "❌ {name} خرج برّا في وسط الجولة (السكور: {score}).",
    returnedCards: ({ n }) => `📥 {name} رجّع ${cards(n)} للكُدس.`,
    wonAllOut: "🏆 {name} ربح الطرح: الباقي الكل خرجو برّا!",
    cancelledNemna3: "{name} بطّل المنعة.",
    cancelledNemna3Penalty: "{name} بطّل المنعة (+{n} بونتو خطيّة).",
    refusedPoseNoPureRun: "{name} حاول يمنع والمنعة ما تقبلتش: لازم سويت من غير جوكر (+{n} بونتو خطيّة).",
    refusedPoseThreshold:
      "{name} حاول يمنع والمنعة ما تقبلتش: عندو {pts} بونتو والمنعة {threshold} (+{n} بونتو خطيّة).",
    cancelledNefrech: "{name} بطّل التفريش.",
    cancelledNefrechPenalty: "{name} بطّل التفريش (+{n} بونتو).",
    redealt: "فريش! الأوراق تفرّقو من جديد — {name} يبدا بـ15 ورقة.",
    choseContinue: "{name} قال: ألعب — الجولة تكمّل.",
    timeUpTurn: "⏱ الوقت وفى: القهواجي لعب في بلاصة {name}.",
    timeUpDraw: "⏱ الوقت وفى: القهواجي هز ورقة من البيوش لـ{name}.",
    timeUpVote: "⏱ الوقت وفى: {name} ما ختارش، حطّينالو فريش.",
    wantsRedealAll: "{name} قال: فريش ({a}/{b}) — نعاودو التفريق.",
    wantsRedealNext: "{name} قال: فريش ({a}/{b}) — الدور على {next}.",
    noPose: "{name} ما منعش ← +{n} بونتو.",
    continuedNoPose: "{name} شدّ الفريش وما منعش ← +{n} بونتو.",
    endsWithCards: ({ n }) => `{name} كمّل الجولة و${cards(n)} في اليد ← +{pts} بونتو.`,
    endsWithCardsJokers: ({ n }) =>
      `{name} كمّل الجولة و${cards(n)} في اليد (منهم {jokers} جوكر) ← +{pts} بونتو.`,
    eliminated: ["{name} خرج برّا (السكور: {score}). 👋", "{name} طاح من الطرح (السكور: {score})."],
    wonGame: "🏆 {name} ربح الطرح! 👑",
    wonGameTwo: "🏆 {name} ربح الطرح، و{second} جا الثاني! 👑",

    // Errors with variables
    notYourTurnOf: "موش دورك (الدور على {name})",
    cantDrawNow: "ما تنجّمش تهزّ ورقة توّا",
    cantDiscardNow: "ما تنجّمش تطيّش توّا",
    cantPoseNow: "ما تنجّمش تنزّل توّا",
    fausseNeedsDrawPhase: "باش تهزّ من الكُدس لازم تكون في وقت البيوش",
    fausseMustBeUsedPenalty:
      "الورقة إلّي هزّيتها من الكُدس لازم تمنع بيها. (+{n} بونتو خطيّة)",
    thresholdMissing:
      "ما وصلتش للمنعة: نزّلت {pts} بونتو، ناقصك {missing} (المنعة {threshold} بونتو). زيد أوراق ولّا بطّل قبل ما تطيّش.",
    poseTooLow: "ما وصلتش للمنعة: عندك {pts} بونتو، والمنعة {threshold}.",
    cantReplaceInSet: "الورقة هاذي ما تعوّضش الجوكر في البريلان هذا: {err}",
    invalidAfterSwap: "المجموعة تفسد كي تعوّض: {err}",
    invalidAfterSwapPenalty: "المجموعة تفسد كي تعوّض: {err} (+{n} بونتو خطيّة)",
    cantAddToSet: "ما تتزادش للبريلان هذا: {err}",
    max14Group:
      "ما تنجّمش تنزّل أكثر من 14 ورقة في الدور، بالأوراق إلّي زدتهم على المجموعات (جرّبت {n}). آخر مجموعة تبطّلت.",
    max14Add:
      "ما تنجّمش تنزّل أكثر من 14 ورقة في الدور، بالأوراق إلّي زدتهم على المجموعات (جرّبت {n}). آخر زيادة تبطّلت.",
    max14Swap:
      "ما تنجّمش تنزّل أكثر من 14 ورقة في الدور (جرّبت {n}، بتعويضات الجوكر). التعويض تبطّل.",
    usefulDiscardErr:
      "الورقة {card} تنجّم تتلعب على {kind} متاع {owner}. رجعتلك، +{n} بونتو خطيّة.",
    cardFitsCombo: "الورقة هاذي تنجّم تتزاد على {kind} محطوط",
    jokerStandsFor: "الجوكر هذا في بلاصة {rank} في السويت — كان الورقة هاذيكة تعوّضو.",
    cancelPenalty: "بطّلت: +{n} بونتو",
    notYourVote: "موش دورك في التصويت (الدور على {name})",
    voteSaved: "صوتك تسجّل — الدور على {name}",
    needExactPlayers: "يلزم {n} لاعبين بالضبط باش نبداو",
    botsRange: "عدد البوتات لازم يكون بين 0 و{n}",
    penCard: "{card}",
  },

  // Fixed backend sentences, keyed by the exact French text.
  srvExact: {
    // Turn / draw / discard
    "Ce n'est pas votre tour": "موش دورك، استنّى شويّة 😉",
    "Joueur éliminé": "راك خرجت من الطرح",
    "Pioche vide, manche terminée": "البيوش وفى، والجولة وفات معاه",
    "Carte piochée": "هزّيت ورقة",
    "Défausse vide": "الكُدس فارغ",
    "La Fausse est vide": "الكُدس فارغ",
    "Premier tour : vous devez piocher depuis la pioche, pas depuis la défausse.":
      "أوّل دور: لازم تبيوشي من البيوش، موش من الكُدس.",
    "La défausse est verrouillée ce tour-ci (cartes héritées d'un joueur éliminé). Vous devez piocher depuis la pioche.":
      "الكُدس مسكّر الدور هذا (أوراق لاعب خرج). لازم تبيوشي من البيوش.",
    "La première défausse d'un joueur ne peut pas être récupérée par le joueur suivant. Piochez depuis la pioche.":
      "أوّل ورقة يطيّشها لاعب ما ينجّمش يهزّها إلّي بعدو. بيوشي من البيوش.",
    "Carte prise de la défausse": "هزّيت الورقة من الكُدس",
    "Carte prise + pose effectuée": "هزّيت الورقة ومنعت 👌",
    "Impossible d'agir pendant le vote de redonne": "ما تنجّم تعمل شي والجماعة تصوّت على الفريش",
    "La carte prise de la Fausse doit être utilisée dans un de vos groupes ou ajouts.":
      "الورقة إلّي هزّيتها من الكُدس لازم تلعبها في مجموعة ولّا زيادة.",
    "Vous devez d'abord piocher une carte avant de défausser (cycle 14 → pioche → 15 → défausse → 14).":
      "هزّ ورقة قبل ما تطيّش (14 ورقة، تهزّ وحدة، ومبعد تطيّش وحدة).",
    "Carte introuvable dans votre main": "الورقة هاذي موش في يدّك",
    "Un Joker ne peut être défaussé que si toutes les cartes restantes sont aussi des Jokers.":
      "الجوكر ما يتطيّش كان كي ما يقعد في يدّك كان الجوكرات.",
    "Carte défaussée": "طيّشت الورقة",

    // Groups
    "Chaque groupe doit contenir au moins 3 cartes": "كل مجموعة يلزمها 3 أوراق على الأقل",
    "Une carte est utilisée dans plusieurs groupes": "فمّا ورقة مستعملة في أكثر من مجموعة",
    "Une carte dans plusieurs groupes": "فمّا ورقة مستعملة في أكثر من مجموعة",
    "Carte absente de la main": "الورقة موش في يدّك",
    "Carte absente de votre main": "الورقة موش في يدّك",
    "Carte absente": "الورقة موش في يدّك",
    "La carte à défausser ne peut pas être dans un groupe":
      "الورقة إلّي باش تطيّشها ما تنجّمش تكون في مجموعة",
    "Vous ne pouvez pas poser avant votre 2ème tour": "ما تنجّمش تمنع قبل الدور الثاني متاعك",
    "Pose invalide: au moins une suite pure sans Joker requise":
      "ما تجيش: لازم على الأقل سويت من غير جوكر",
    "Vous devez inclure au moins une suite pure de même couleur sans Joker":
      "لازم على الأقل سويت من نفس اللون من غير جوكر",
    "Il faut au moins une suite pure sans Joker": "لازم على الأقل سويت من غير جوكر",
    "Aucune combinaison fournie": "ما نزّلت حتى مجموعة",
    "La carte de la défausse doit être utilisée dans Nemna3": "ورقة الكُدس لازم تمنع بيها",
    "Vous devez garder au moins une carte en main pour défausser. C'est en la défaussant que vous gagnez la manche.":
      "خلّي ورقة على الأقل باش تطيّشها: كي تطيّشها تفرش.",
    "Vous devez garder au moins une carte pour défausser. C'est en la défaussant que vous gagnez la manche.":
      "خلّي ورقة على الأقل باش تطيّشها: كي تطيّشها تفرش.",

    // Additions
    "Ajout vide": "ما زدت حتى ورقة",
    "Joueur cible introuvable": "اللاعب هذا ما لقيناهش",
    "Joueur cible introuvable (remplacement)": "اللاعب هذا ما لقيناهش",
    "Joueur cible introuvable pour ajout": "اللاعب هذا ما لقيناهش",
    "Groupe cible introuvable": "المجموعة هاذي ما لقيناهاش",
    "Groupe cible introuvable (remplacement)": "المجموعة هاذي ما لقيناهاش",
    "Ce groupe est complet (4 cartes) — aucun ajout possible":
      "المجموعة هاذي كاملة (4 أوراق) — ما عادش تزيد عليها",
    "Ce groupe est complet (4 cartes)": "المجموعة هاذي كاملة (4 أوراق)",
    "Cette carte est déjà utilisée ou est la carte à défausser":
      "الورقة هاذي مستعملة ولّا هي إلّي باش تطيّشها",
    "Ce brelan est verrouillé (complété sans Joker)": "البريلان هذا مسكّر (كمل بلاش جوكر)",
    "Ce brelan est verrouillé (complété sans Joker) — aucun ajout possible":
      "البريلان هذا مسكّر (كمل بلاش جوكر) — ما عادش تزيد عليه",
    "Ce brelan est verrouillé — aucun ajout possible":
      "البريلان هذا مسكّر — ما تزيدش عليه",
    "Une carte est utilisée plusieurs fois": "فمّا ورقة مستعملة أكثر من مرّة",
    "Une carte est utilisée plusieurs fois (remplacement)": "فمّا ورقة مستعملة أكثر من مرّة",
    "Une carte utilisée plusieurs fois": "فمّا ورقة مستعملة أكثر من مرّة",
    "Carte d'ajout absente de la main": "الورقة إلّي تحبّ تزيدها موش في يدّك",
    "La carte à défausser ne peut pas être ajoutée à un groupe":
      "الورقة إلّي باش تطيّشها ما تتزادش لمجموعة",
    "Ajout invalide à un groupe existant (suite invalide ou cette carte déplacerait un Joker déjà posé).":
      "الزيادة ما تجيش (السويت تفسد ولّا الورقة تحرّك جوكر محطوط).",
    "Ajout invalide (suite invalide ou cette carte déplacerait un Joker déjà posé).":
      "الزيادة ما تجيش (السويت تفسد ولّا الورقة تحرّك جوكر محطوط).",
    "Impossible d'ajouter maintenant (pas en phase d'action)": "ما تنجّمش تزيد توّا",
    "Vous devez d'abord poser des combinaisons valides avant d'ajouter à un groupe":
      "امنع قبل، ومبعد زيد على المجموعات",
    "Impossible d'ajouter: la suite ne reste pas valide (ou cette carte déplacerait un Joker déjà posé).":
      "ما تتزادش: السويت تفسد (ولّا الورقة تحرّك جوكر محطوط).",
    "La suite ne reste pas valide après ajout (ou cette carte déplacerait un Joker déjà posé).":
      "السويت تفسد بالزيادة هاذي (ولّا الورقة تحرّك جوكر محطوط).",
    "Cette carte n'a pas été ajoutée à ce groupe ce tour":
      "الورقة هاذي ما زدتهاش للمجموعة في الدور هذا",
    "Carte introuvable dans le groupe": "الورقة موش في المجموعة",
    "Ajout annulé": "الزيادة تبطّلت",
    "Aucune modification à annuler sur ce groupe": "ما فمّا شي تبطّلو في المجموعة هاذي",
    "Impossible d'annuler : une carte retirée du groupe n'est plus en main (a-t-elle été posée ailleurs ?).":
      "ما تنجّمش تبطّل: فمّا ورقة تنحّات من المجموعة وما عادش في يدّك (بالكشي حطّيتها في بلاصة أخرى؟).",
    "Modifications annulées": "رجّعنا كل شي كيما كان",

    // Jokers
    "Pour récupérer le Joker de ce brelan, posez une seule carte réelle":
      "باش تهزّ الجوكر من البريلان هذا، حطّ ورقة صحيحة وحدة برك",
    "Carte de remplacement absente de la main": "الورقة إلّي باش تعوّض بيها موش في يدّك",
    "Carte de remplacement introuvable dans votre main": "الورقة إلّي باش تعوّض بيها موش في يدّك",
    "Carte de remplacement introuvable dans le groupe": "الورقة إلّي عوّضت بيها موش في المجموعة",
    "Un Joker ne peut pas remplacer un autre Joker dans ce brelan":
      "جوكر ما يعوّضش جوكر في البريلان هذا",
    "On ne peut pas remplacer un Joker par un autre Joker": "جوكر ما يعوّضش جوكر",
    "Vous ne pouvez pas remplacer un Joker par un autre Joker": "جوكر ما يعوّضش جوكر",
    "Pour récupérer un Joker, vous devez d'abord poser des combinaisons valides (seuil Nemna3 atteint).":
      "باش تهزّ جوكر، لازم تمنع قبل (توصل للمنعة).",
    "Vous devez d'abord poser des combinaisons valides avant de récupérer un Joker":
      "امنع قبل، ومبعد هزّ الجوكر",
    "Un Joker récupéré ce tour attend votre pose : posez vos groupes, ou annulez.":
      "الجوكر إلّي خطفتو الدور هذا يستنّى المنعة متاعك: نزّل مجموعاتك ولّا بطّل.",
    "Joker introuvable dans ce groupe": "ما فمّاش جوكر في المجموعة هاذي",
    "Joker introuvable dans ce groupe (remplacement)": "ما فمّاش جوكر في المجموعة هاذي",
    "Joker introuvable dans la main (récupération déjà annulée ?)":
      "الجوكر موش في يدّك (بالكشي رجّعتو ديجا؟)",
    "La carte à défausser ne peut pas servir de remplacement de Joker":
      "الورقة إلّي باش تطيّشها ما تعوّضش الجوكر",
    "Groupe de 3 cartes : pour récupérer le Joker, fournissez 2 cartes (remplacement + 4e d'un type différent).":
      "مجموعة بـ3 أوراق: باش تهزّ الجوكر حطّ زوز أوراق (إلّي تعوّضو + رابعة من لون آخر).",
    "Ce groupe ne contient que 3 cartes : pour récupérer le Joker, vous devez fournir 2 cartes (la carte manquante + une 4e d'un type différent).":
      "المجموعة فيها 3 أوراق برك: باش تهزّ الجوكر حطّ زوز أوراق (الناقصة + رابعة من لون آخر).",
    "Ce groupe ne contient que 3 cartes : 2 cartes nécessaires.":
      "المجموعة فيها 3 أوراق برك: يلزمك زوز أوراق.",
    "La carte à défausser ne peut pas servir de carte additionnelle":
      "الورقة إلّي باش تطيّشها ما تنجّمش تكون الورقة الزايدة",
    "Carte additionnelle absente de la main": "الورقة الزايدة موش في يدّك",
    "Carte additionnelle introuvable dans votre main": "الورقة الزايدة موش في يدّك",
    "La carte additionnelle ne peut pas être un Joker": "الورقة الزايدة ما تنجّمش تكون جوكر",
    "La carte additionnelle doit être différente de la carte de remplacement":
      "الورقة الزايدة لازم تكون غير إلّي تعوّض بيها",
    "La carte de remplacement ne peut pas être dans les groupes posés":
      "الورقة إلّي تعوّض بيها ما تنجّمش تكون في المجموعات إلّي نزّلتهم",
    "La carte additionnelle ne peut pas être dans les groupes posés":
      "الورقة الزايدة ما تنجّمش تكون في المجموعات إلّي نزّلتهم",
    "Placement Joker impossible à gauche : aucune carte n'existe avant ce rang dans la suite.":
      "الجوكر ما يتحطّش على اليسار: ما فمّاش ورقة قبل في السويت.",
    "Placement Joker impossible à droite : aucune carte n'existe après ce rang dans la suite.":
      "الجوكر ما يتحطّش على اليمين: ما فمّاش ورقة بعد في السويت.",
    "Position invalide pour ce Joker : la suite ne reste pas valide. Essayez l'autre côté.":
      "البلاصة هاذي ما تجيش للجوكر: السويت تفسد. جرّب الجيهة الأخرى.",
    "Joker récupéré depuis le brelan": "خطفت الجوكر من البريلان 😎",
    "Joker récupéré": "الجوكر رجعلك 😎",
    "Récupération annulée": "رجّعت الجوكر لبلاصتو",
    "Aucun Joker remplaçable par cette carte": "الورقة هاذي ما تعوّض حتى جوكر",

    // Nefrech / vote / round
    "Impossible de gagner la manche maintenant": "ما تنجّمش تفرش توّا",
    "Main insuffisante pour gagner la manche": "أوراقك ما يكفيوش باش تفرش",
    "Carte à défausser introuvable": "الورقة إلّي باش تطيّشها ما لقيناهاش",
    "Toutes les cartes (sauf la défausse) doivent être placées (groupe ou ajout)":
      "الأوراق الكل (كان إلّي تطيّشها) لازم يتحطّو (مجموعة ولّا زيادة)",
    "Manche gagnée": "فرشت! 🔥",
    "Manche gagnée — Victoire !": "فرشت — ربحت الجولة! 🏆",
    "Pas en phase de vote de redonne": "موش وقت التصويت على الفريش",
    "Vote déjà résolu": "التصويت وفى ديجا",
    "Joueur introuvable ou éliminé": "اللاعب ما لقيناهش ولّا خرج",
    "Choix invalide : 'rejouer' ou 'continuer'": "اختيار غالط: «فريش» ولّا «ألعب»",
    "La manche précédente n'est pas terminée": "الجولة إلّي قبل مازالت ما وفاتش",
    "Partie terminée : il ne reste plus que des bots, tous les joueurs réels sont éliminés.":
      "الطرح وفى: ما قعدو كان البوتات، اللاعبين الكل خرجو. 🤖",
    "Partie terminée : tous les joueurs sont éliminés.": "الطرح وفى: اللاعبين الكل خرجو.",
    "La pioche a été remélangée depuis la défausse.":
      "البيوش وفى — خلّطنا الكُدس ورجّعناه بيوش.",

    // Combination validation
    "Une combinaison doit contenir au moins 3 cartes": "المجموعة يلزمها 3 أوراق على الأقل",
    "Trop de cartes dans la combinaison": "برشا أوراق في المجموعة",
    "Un brelan/carré ne peut pas dépasser 4 cartes": "البريلان/الكاري ما يفوتش 4 أوراق",
    "Le brelan/carré doit contenir au moins 2 vraies cartes":
      "البريلان/الكاري يلزمو على الأقل زوز أوراق صحاح",
    "Toutes les cartes du brelan doivent avoir la même valeur":
      "أوراق البريلان لازم يكونو نفس القيمة",
    "Impossible d'avoir deux cartes identiques dans un brelan":
      "ما تنجّمش تحطّ زوز أوراق كيف كيف في بريلان",
    "Trop de jokers pour un brelan/carré": "برشا جوكرات على بريلان/كاري",
    "Deux Jokers ne peuvent pas se suivre dans une suite": "زوز جوكر ما ينجموش يكونو حذا بعضهم في السويت",
    "La suite doit contenir au moins 2 vraies cartes (max 1 Joker par tranche de 2 cartes)":
      "السويت يلزمها على الأقل زوز أوراق صحاح (جوكر واحد لكل زوز أوراق)",
    "Toutes les cartes d'une suite doivent être de la même couleur":
      "أوراق السويت لازم يكونو من نفس اللون",
    "Une suite ne peut pas contenir deux cartes identiques": "السويت ما فيهاش زوز أوراق كيف كيف",
    "Séquence invalide (les cartes doivent être consécutives)":
      "السويت غالطة (الأوراق لازم يكونو ورا بعضهم)",

    // Round summary penalty lines
    "Pénalité défausse utile": "خطيّة: طيّش ورقة بلاس",
    "Pénalité standard (Nemna3 annulé ou refusé)": "الخطيّة العاديّة (المنعة تبطّلت ولّا ما تقبلتش)",
    "Pénalité — défausse utilisée par le gagnant": "خطيّة: الرابح فرش من كفو",
    "Aucune combinaison posée pendant la manche": "ما منعش في الجولة هاذي",
    "A choisi de continuer sans poser de combinaison": "شدّ الفريش وما منعش",
    "Joker": "جوكر",

    // Rooms / lobby
    "Joueur introuvable": "اللاعب ما لقيناهش",
    "Joueur introuvable dans la partie": "اللاعب موش في الطرح",
    "La partie a déjà commencé": "الطرح بدا ديجا",
    "Partie déjà démarrée": "الطرح بدا ديجا",
    "Partie non démarrée": "الطرح مازال ما بداش",
    "Seul l'hôte peut configurer les bots": "كان مولى الطرح ينجّم يبدّل البوتات",
    "Impossible : il y a déjà trop de joueurs humains pour ce nombre de bots":
      "ما تجيش: فمّا برشا لاعبين للعدد هذا من البوتات",
    "Un bot ne peut pas quitter": "البوت ما يخرجش",
    "Joueur déjà sorti de la partie": "اللاعب خرج ديجا",
    "Partie quittée": "خرجت من الطرح",
    "Un bot ne peut pas remplacer un joueur": "بوت ما يعوّضش لاعب",
    "Seul l'hôte peut remplacer un joueur par un bot": "كان مولى الطرح ينجّم يعوّض لاعب ببوت",
    "L'hôte ne peut pas se remplacer lui-même": "مولى الطرح ما يعوّضش روحو",
    "Ce slot est déjà un bot": "البلاصة هاذي فيها بوت ديجا",
    "Un joueur éliminé n'a pas besoin d'être remplacé par un bot":
      "لاعب خرج ما يستحقّش بوت في بلاصتو",
    "Ce joueur est toujours connecté": "اللاعب هذا مازال كونيكتي",
    "Ce joueur regarde une pub, il revient": "اللاعب هذا يتفرّج في إشهار، توّا يرجع",
    "Joueur remplacé par un bot": "بوت خذا بلاصتو",
    "Room introuvable": "الطرح ما لقيناهش",
    "Salle introuvable": "الطرح ما لقيناهش",
    "Salle introuvable ou lien expiré": "الطرح ما لقيناهش ولّا اللينك وفى",
    "Room pleine": "الطرح معبّي",
    "Cette salle est réservée aux joueurs de la partie précédente":
      "الطرح هذا محجوز للّي لعبو الطرح إلّي فات",
    "La partie n'est pas terminée": "الطرح مازال ما وفاش",
    "Couleur de table inconnue": "لون الطاولة هذا ما نعرفوهش",
    "Impossible de quitter une partie en cours": "ما تنجّمش تخرج والطرح يتلعب",
    "Seul l'hôte peut démarrer": "كان مولى الطرح ينجّم يبدا",
    "Il faut au moins 2 joueurs pour démarrer": "يلزم زوز لاعبين على الأقل باش نبداو",
    "Seul l'hôte peut lancer la partie": "كان مولى الطرح ينجّم يبدا",
    "Seul l'hôte peut relancer la manche": "كان مولى الطرح ينجّم يبدا الجولة الجاية",
    "Erreur de matchmaking": "ما نجّمناش نلقاولك طرح",
    "Cette partie n'existe plus": "الطرح هذا ما عادش موجود",
    "Vous avez été remplacé par un bot": "بوت خذا بلاصتك",
    "Vous avez quitté cette partie": "خرجت من الطرح هذا",
    // Invitations (backend/social.py, server.py)
    "Tu ne peux pas t'inviter toi-même": "ما تنجّمش تستدعي روحك",
    "Ce joueur a refusé ton invitation, réessaie plus tard": "اللاعب هذا رفض الإستدعاء متاعك، عاود من بعد",
    "Trop d'invitations envoyées, réessaie dans un moment": "بعثت برشا إستدعاءات، عاود بعد شوية",
    "Cette invitation n'est plus valable": "الإستدعاء هذا ما عادش صالح",
    "Termine ta partie avant d'inviter": "كمّل الطرح متاعك قبل ما تستدعي",
    "Ce joueur est déjà à ta table": "اللاعب هذا راهو معاك في الطاولة",
    "Ta table est complète": "الطاولة متاعك معبّية",
    "On n'invite pas dans une partie en ligne": "ما تنجّمش تستدعي في طرح أونلاين",
    "Termine ta partie avant d'en rejoindre une autre": "كمّل الطرح متاعك قبل ما تدخل لواحد آخر",

    // Auth
    "Non authentifié": "لازم تدخل قبل",
    "Session invalide": "السيسيون وفات، عاود ادخل",
    "Session expirée": "السيسيون وفات، عاود ادخل",
    "Utilisateur introuvable": "الكونت ما لقيناهش",
    "Le nom est requis": "اكتب اسمك قبل",
    // The guest kept on its device (backend/auth.py)
    "Profil invité introuvable": "حساب الضيف هذا ما عادش موجود",
    "Clé d'appareil invalide": "الجهاز هذا ما عرفناهش",
    "Réservé aux profils invités": "هاذي للضيوف برك",
    // Profile (backend/player_profile.py)
    "Le nom doit faire au moins 2 caractères": "الإسم لازم فيه زوز حروف على الأقل",
    "Le nom ne peut pas dépasser 20 caractères": "الإسم ما يفوتش 20 حرف",
    "Ce nom est réservé aux bots": "الإسم هذا متاع البوتات",
    "Icône inconnue": "الصورة هاذي ما نعرفوهاش",
    "Trop de changements, réessaie dans quelques minutes": "بدّلت برشا، عاود بعد شوية",
  },
};

export default tn;
